import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/hooks/useApp';
import { useSeo } from '@/hooks/useSeo';
import { SITE_NAME } from '@/lib/seo';
import { ApiError, api } from '@/lib/api';
import { Button, Field, Input, Spinner } from '@/components/ui';
import { Logo } from '@/components/Logo';
import { FacebookIcon, GoogleIcon } from '@/components/SocialIcons';
import type { AuthResponse } from '@/lib/types';

function OAuthButtons({ returnUrl }: { returnUrl: string }) {
  // A full navigation, not fetch: the OAuth flow is a series of cross-site redirects that the
  // browser has to follow itself, and XHR cannot leave the origin and come back with a cookie.
  const start = (provider: string) => {
    window.location.href = `/api/auth/${provider}?returnUrl=${encodeURIComponent(returnUrl)}`;
  };

  /*
    The mark is absolutely positioned rather than sitting in the flex row with the label. Laid out
    inline, each button centres its own icon+text pair as a unit, so "Google" and "Facebook" being
    different widths leaves the two logos at visibly different x-positions. Pinning them to the left
    edge lines them up with each other and lets the label stay centred in the button.
  */
  return (
    <div className="grid gap-2.5">
      <Button
        type="button"
        variant="secondary"
        size="lg"
        className="relative w-full"
        onClick={() => start('google')}
      >
        <GoogleIcon className="absolute left-4 size-5" />
        Nastavi sa Google nalogom
      </Button>
      <Button
        type="button"
        variant="secondary"
        size="lg"
        className="relative w-full"
        onClick={() => start('facebook')}
      >
        <FacebookIcon className="absolute left-4 size-5" />
        Nastavi sa Facebook nalogom
      </Button>
    </div>
  );
}

function AuthShell({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle: string;
  children: React.ReactNode;
}) {
  return (
    <div className="relative overflow-hidden">
      <div className="aurora">
        <span />
      </div>

      <div className="relative mx-auto flex max-w-md flex-col px-4 py-12 sm:py-16">
        <div className="animate-fade-in-up mb-8 text-center">
          <Logo className="mx-auto mb-6 h-11" title="KojaKola" />
          <h1 className="text-2xl font-bold text-body">{title}</h1>
          <p className="mt-1.5 text-sm text-muted">{subtitle}</p>
        </div>

        <div className="animate-fade-in-up rounded-[var(--radius-panel)] border border-default bg-raised p-6 shadow-card">
          {children}
        </div>
      </div>
    </div>
  );
}

/**
 * The two message banners every auth form needs.
 *
 * `role="alert"` on the failure and `role="status"` on the success is the distinction that matters
 * to a screen reader: one interrupts, the other waits for a pause. Both were about to be pasted a
 * fourth and fifth time, so they live here instead.
 */
function FormError({ children }: { children: React.ReactNode }) {
  return (
    <p
      role="alert"
      className="animate-fade-in-down rounded-xl border border-danger/20 bg-danger/10 p-3 text-sm text-danger"
    >
      {children}
    </p>
  );
}

function FormNotice({ children }: { children: React.ReactNode }) {
  return (
    <p
      role="status"
      className="animate-fade-in-down rounded-xl border border-success/20 bg-success/10 p-3 text-sm text-success"
    >
      {children}
    </p>
  );
}

export function LoginPage() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  useSeo({ title: `Prijava | ${SITE_NAME}`, noIndex: true });

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const returnUrl = (location.state as { from?: string } | null)?.from ?? '/';

  useEffect(() => {
    if (user) {
      navigate(returnUrl, { replace: true });
    }
  }, [user, navigate, returnUrl]);

  useEffect(() => {
    // The OAuth callback redirects here with a reason when it could not complete, so the visitor
    // is told what happened instead of silently landing back on an empty form.
    const reason = searchParams.get('greska');
    if (reason === 'email') {
      setError('Nalog nema e-mail adresu. Prijavite se lozinkom ili dodajte e-mail kod provajdera.');
    } else if (reason === 'oauth') {
      setError('Prijava preko društvene mreže nije uspela. Pokušajte ponovo.');
    } else if (reason === 'nedostupno') {
      setError('Prijava preko društvene mreže trenutno nije dostupna.');
    }

    // Resetting the password signs every session out, so the visitor lands back here and needs to
    // be told the reset worked — otherwise being asked to sign in again reads as a failure.
    if (searchParams.get('poruka') === 'lozinka-promenjena') {
      setNotice('Lozinka je promenjena. Prijavite se novom lozinkom.');
    }
  }, [searchParams]);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setBusy(true);

    try {
      await login(email, password);
      navigate(returnUrl, { replace: true });
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Prijava nije uspela.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell title="Prijava" subtitle="Dobrodošli nazad">
      <form onSubmit={submit} className="space-y-4">
        <Field label="E-mail" required>
          <Input
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </Field>

        <Field label="Lozinka" required>
          <Input
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </Field>

        {notice && <FormNotice>{notice}</FormNotice>}
        {error && <FormError>{error}</FormError>}

        <Button type="submit" size="lg" className="w-full" loading={busy}>
          Prijavi se
        </Button>
      </form>

      <div className="my-5 flex items-center gap-3 text-xs text-faint">
        <span className="h-px flex-1 bg-[var(--border)]" />
        ili
        <span className="h-px flex-1 bg-[var(--border)]" />
      </div>

      <OAuthButtons returnUrl={returnUrl} />

      <div className="mt-6 space-y-2 text-center text-sm">
        <p>
          <Link to="/zaboravljena-lozinka" className="font-medium text-muted hover:text-accent hover:underline">
            Zaboravljena lozinka?
          </Link>
        </p>
        <p className="text-muted">
          Nemate nalog?{' '}
          <Link to="/registracija" className="font-medium text-accent hover:underline">
            Registrujte se
          </Link>
        </p>
      </div>
    </AuthShell>
  );
}

export function RegisterPage() {
  const { register, user } = useAuth();
  const navigate = useNavigate();

  useSeo({ title: `Registracija | ${SITE_NAME}`, noIndex: true });

  const [form, setForm] = useState({ email: '', password: '', displayName: '' });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user) {
      navigate('/', { replace: true });
    }
  }, [user, navigate]);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setBusy(true);

    try {
      await register(form.email, form.password, form.displayName);
      navigate('/', { replace: true });
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Registracija nije uspela.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell title="Registracija" subtitle="Besplatno, bez reklama">
      <form onSubmit={submit} className="space-y-4">
        <Field label="Ime" required>
          <Input
            autoComplete="name"
            required
            value={form.displayName}
            onChange={(event) => setForm({ ...form, displayName: event.target.value })}
          />
        </Field>

        <Field label="E-mail" required>
          <Input
            type="email"
            autoComplete="email"
            required
            value={form.email}
            onChange={(event) => setForm({ ...form, email: event.target.value })}
          />
        </Field>

        <Field label="Lozinka" hint="Najmanje 10 karaktera" required>
          <Input
            type="password"
            autoComplete="new-password"
            required
            minLength={10}
            value={form.password}
            onChange={(event) => setForm({ ...form, password: event.target.value })}
          />
        </Field>

        {error && <FormError>{error}</FormError>}

        <Button type="submit" size="lg" className="w-full" loading={busy}>
          Napravi nalog
        </Button>
      </form>

      <div className="my-5 flex items-center gap-3 text-xs text-faint">
        <span className="h-px flex-1 bg-[var(--border)]" />
        ili
        <span className="h-px flex-1 bg-[var(--border)]" />
      </div>

      <OAuthButtons returnUrl="/" />

      <p className="mt-6 text-center text-sm text-muted">
        Već imate nalog?{' '}
        <Link to="/prijava" className="font-medium text-accent hover:underline">
          Prijavite se
        </Link>
      </p>
    </AuthShell>
  );
}

/**
 * Asks for an address and sends a reset link to it.
 *
 * The confirmation replaces the form rather than sitting above it. Leaving the form there invites a
 * second and third submission from someone who cannot see a mail that has not arrived yet, and each
 * one invalidates the link in the message before it.
 */
export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useSeo({ title: `Zaboravljena lozinka | ${SITE_NAME}`, noIndex: true });

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setBusy(true);

    try {
      await api<{ message: string }>('/api/auth/forgot-password', {
        method: 'POST',
        body: { email },
      });
      setSent(true);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Slanje nije uspelo. Pokušajte ponovo.');
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <AuthShell title="Proverite e-mail" subtitle="Poslali smo vam link">
        <div className="space-y-4 text-center">
          <p className="text-sm text-muted">
            Ako nalog sa adresom <span className="font-medium text-body">{email}</span> postoji,
            poslali smo mu link za promenu lozinke.
          </p>
          <p className="text-sm text-muted">
            Link važi 3 sata. Ako ga nema u prijemnom sandučetu, proverite i neželjenu poštu.
          </p>
          <Link to="/prijava" className="inline-block pt-2 text-sm font-medium text-accent hover:underline">
            Nazad na prijavu
          </Link>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Zaboravljena lozinka" subtitle="Poslaćemo vam link za promenu">
      <form onSubmit={submit} className="space-y-4">
        <Field label="E-mail" hint="Adresa sa kojom ste otvorili nalog" required>
          <Input
            type="email"
            autoComplete="email"
            autoFocus
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </Field>

        {error && <FormError>{error}</FormError>}

        <Button type="submit" size="lg" className="w-full" loading={busy}>
          Pošalji link
        </Button>
      </form>

      <p className="mt-6 text-center text-sm text-muted">
        Setili ste se lozinke?{' '}
        <Link to="/prijava" className="font-medium text-accent hover:underline">
          Prijavite se
        </Link>
      </p>
    </AuthShell>
  );
}

/**
 * Where the link in the reset email lands.
 *
 * The address and token arrive in the query string and are never shown or edited — they are proof,
 * not input. A malformed or missing pair is caught before the form is offered, so nobody types a new
 * password twice only to be told the link was broken all along.
 */
export function ResetPasswordPage() {
  const { adopt } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const email = searchParams.get('email') ?? '';
  const token = searchParams.get('token') ?? '';

  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // noIndex matters more here than on the other auth pages: this URL carries a live reset token in
  // its query string, and an indexed copy would put it in a public search result.
  useSeo({ title: `Nova lozinka | ${SITE_NAME}`, noIndex: true });

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();

    // Checked here rather than only by the browser's own validity rules, because the two fields are
    // valid individually and it is their disagreement that matters.
    if (password !== confirmation) {
      setError('Lozinke se ne poklapaju.');
      return;
    }

    setError(null);
    setBusy(true);

    try {
      const auth = await api<AuthResponse>('/api/auth/reset-password', {
        method: 'POST',
        body: { email, token, newPassword: password },
      });

      // The server signs them in as part of the reset, so the session is adopted rather than
      // sending someone who has just proved control of the inbox back to a login form.
      adopt(auth);
      navigate('/', { replace: true });
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Promena lozinke nije uspela.');
    } finally {
      setBusy(false);
    }
  };

  if (!email || !token) {
    return (
      <AuthShell title="Neispravan link" subtitle="Link nije potpun">
        <div className="space-y-4 text-center">
          <p className="text-sm text-muted">
            Link za promenu lozinke nije ispravan. Moguće je da je isečen prilikom kopiranja iz
            e-maila.
          </p>
          <Link
            to="/zaboravljena-lozinka"
            className="inline-block pt-2 text-sm font-medium text-accent hover:underline"
          >
            Zatražite novi link
          </Link>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Nova lozinka" subtitle={email}>
      <form onSubmit={submit} className="space-y-4">
        {/*
          Hidden, but present: password managers read the surrounding form to decide which entry to
          update, and without a username field they save the new password as a brand new login.
        */}
        <input type="hidden" name="username" autoComplete="username" value={email} readOnly />

        <Field label="Nova lozinka" hint="Najmanje 10 karaktera" required>
          <Input
            type="password"
            autoComplete="new-password"
            autoFocus
            required
            minLength={10}
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </Field>

        <Field label="Potvrdite lozinku" required>
          <Input
            type="password"
            autoComplete="new-password"
            required
            minLength={10}
            value={confirmation}
            onChange={(event) => setConfirmation(event.target.value)}
          />
        </Field>

        {error && <FormError>{error}</FormError>}

        <Button type="submit" size="lg" className="w-full" loading={busy}>
          Sačuvaj lozinku
        </Button>
      </form>

      <p className="mt-6 text-center text-sm text-muted">
        Link više ne radi?{' '}
        <Link to="/zaboravljena-lozinka" className="font-medium text-accent hover:underline">
          Zatražite novi
        </Link>
      </p>
    </AuthShell>
  );
}

/**
 * Where the OAuth redirect lands.
 *
 * The server puts the access token in the URL fragment, which browsers never send to servers and
 * never write into referrer headers. It is read once, exchanged for an in-memory session, and the
 * fragment is stripped from history so it cannot be recovered from the back button.
 */
export function AuthCallbackPage() {
  const { adopt } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const fragment = new URLSearchParams(window.location.hash.slice(1));
    const accessToken = fragment.get('access_token');
    const next = searchParams.get('next') ?? '/';

    if (!accessToken) {
      navigate('/prijava?greska=oauth', { replace: true });
      return;
    }

    window.history.replaceState(null, '', window.location.pathname);

    (async () => {
      try {
        // The refresh cookie is already set, so this both proves the session works and yields the
        // user record without needing the server to encode it into the redirect.
        const auth = await api<AuthResponse>('/api/auth/refresh', { method: 'POST', body: {} });
        adopt(auth);
        navigate(next, { replace: true });
      } catch {
        navigate('/prijava?greska=oauth', { replace: true });
      }
    })();
  }, [adopt, navigate, searchParams]);

  return (
    <div className="flex flex-col items-center justify-center gap-3 py-24">
      <Spinner className="size-8 text-accent" />
      <p className="text-sm text-muted">Prijavljivanje…</p>
    </div>
  );
}
